import { useTranslation } from "react-i18next";
import mailIcon from "@surgia/ui/icons/popup/email.svg"; // adjust path as needed
import { countries } from "../../../../../packages/contract/constants/countryCodes";
import sortIcon from "@surgia/ui/icons/table/sort.svg"; // for dropdown arrow
import { useState, useRef, useEffect } from "react";

const institutionTypes = [
  { value: "HOSPITAL", labelKey: "shared.entityType.HOSPITAL" },
  { value: "POLY_CLINIC", labelKey: "shared.entityType.POLY_CLINIC" },
  { value: "OTHER", labelKey: "shared.entityType.OTHER" },
];

const CreateTenantForm = ({
  cities = [],
}: {
  cities?: { id: string | number; name: string }[];
}) => {
  const { t } = useTranslation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[1]); // Default to Saudi Arabia
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <form className="grid grid-cols-2 gap-4">
      {/* Institution type */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("Institution type")} <span className="text-[#0033CC]">*</span>
        </label>
        <select className="w-full border rounded-lg px-4 py-2">
          <option value="">{t("Select type")}</option>
          {institutionTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {t(type.labelKey)}
            </option>
          ))}
        </select>
      </div>
      {/* Institution name */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("Institution name")} <span className="text-[#0033CC]">*</span>
        </label>
        <input
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("Enter clinic or hospital name")}
        />
      </div>
      {/* City */}
      <div>
        <label className="block mb-1 text-[14px] ">
          {t("City")} <span className="text-[#0033CC]">*</span>
        </label>
        <select className="w-full border rounded-lg px-4 py-2">
          <option value="">{t("Select city")}</option>
          {cities.map((city) => (
            <option key={city.id} value={city.id}>
              {city.name}
            </option>
          ))}
        </select>
      </div>
      {/* District */}
      <div>
        <label className="block mb-1 text-[14px] ">
          {t("District")} <span className="text-[#0033CC]">*</span>
        </label>
        <input
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("Enter district name")}
        />
      </div>
      {/* Full address */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("Full address")} <span className="text-[#0033CC]">*</span>
        </label>
        <input
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("Enter street name")}
        />
      </div>
      {/* Contact person name */}
      <div>
        <label className="block mb-1 text-[14px] ">
          {t("Contact person name")}
        </label>
        <input
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("Enter first name")}
        />
      </div>
      <div>
        <label className="block mb-1 text-[14px] ">&nbsp;</label>
        <input
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("Enter last name")}
        />
      </div>
      {/* Contact person number */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("Contact person number")} <span className="text-[#0033CC]">*</span>
        </label>
        <div className="flex focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-gray-300 rounded-lg">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex items-center border border-r-0 rounded-l-lg px-3 py-2 bg-white min-w-[60px] focus:outline-none"
              onClick={() => setShowDropdown((v) => !v)}
              tabIndex={0}
            >
              <span className="text-xl">{selectedCountry.flag}</span>
              <span className="ml-2 text-gray-700">{selectedCountry.dial}</span>
              <span className="ml-2">
                <img src={sortIcon} alt="Sort" className="w-4 h-4" />
              </span>
            </button>
            {showDropdown && (
              <div
                className="absolute left-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg z-10"
                style={{ minWidth: "100%", width: "max-content" }}
              >
                {countries.map((country: any) => (
                  <div
                    key={country.code}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                      selectedCountry.dial === country.dial ? "bg-gray-100" : ""
                    }`}
                    onClick={() => {
                      setSelectedCountry(country);
                      setShowDropdown(false);
                    }}
                  >
                    <span className="mr-2 text-xl">{country.flag}</span>
                    <span className="mr-2 text-sm text-gray-700">
                      {t(`shared.branchForm.countries.${country.code}`)}
                    </span>
                    <span className="ml-auto text-gray-500 text-xs">
                      {country.dial}
                    </span>
                    {selectedCountry.dial === country.dial && (
                      <span className="ml-6 flex items-center">
                        {/* You can use a check icon here if you want */}✓
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            className="w-full border-t border-b border-r rounded-r-lg px-4 py-2 focus:outline-none"
            placeholder={t("Enter phone number")}
            type="tel"
          />
        </div>
      </div>
      {/* Contact person Email */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("Contact person Email")} <span className="text-[#0033CC]">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <img src={mailIcon} alt="Mail" className="w-5 h-5" />
          </span>
          <input
            className="w-full border rounded-lg px-4 py-2 pl-10"
            placeholder={t("Enter user email")}
            type="email"
          />
        </div>
      </div>
    </form>
  );
};

export default CreateTenantForm;
